import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { GenerateGuidService } from '../shared/generate-guid/generate-guid.service';
import { HbNotification } from './notification';
import { NotificationMessage } from './notification-message';
import { NotificationState } from './notification-state';

@Injectable()
export class NotificationService {
  private _notifications: BehaviorSubject<HbNotification[]> = new BehaviorSubject([]);
  public readonly notifications: Observable<HbNotification[]> = this._notifications.asObservable();

  private _notificationAdded: Subject<NotificationMessage> = new Subject();
  public readonly notificationAdded: Observable<NotificationMessage> = this._notificationAdded.asObservable();

  constructor(private generateGuidService: GenerateGuidService) {}

  private createNotificationMessage(n: HbNotification): NotificationMessage {
    const message = new NotificationMessage();

    switch (n.state) {
      case NotificationState.Complete:
        message.detail = 'Completed';
        message.severity = 'success';
        break;

      case NotificationState.Failed:
        message.detail = 'Action could not be completed';
        message.severity = 'error';
        break;

      case NotificationState.Processing:
        message.detail = 'Processing...';
        message.severity = 'info';
        break;

      case NotificationState.Queued:
        message.detail = 'Queued...';
        message.severity = 'info';
        break;

      case NotificationState.None:

      default:
        message.severity = 'info';
        break;
    }

    if (n.message) {
      message.detail = n.message;
    }

    message.life = n.life;
    message.closable = n.closable;
    message.summary = n.description;

    return message;
  }

  public addNotification(notification: HbNotification) {
    notification.id = this.generateGuidService.newGuid();
    this._notifications.getValue().push(notification);
    this._notifications.next(this._notifications.getValue());
    this._notificationAdded.next(this.createNotificationMessage(notification));
  }

  public displayToastDetails(message: string, state: NotificationState): void {
    const notification = new HbNotification();

    notification.description = message;
    notification.state = state;
    this.displayToast(notification);
  }

  public displayToast(notification: HbNotification) {
    this._notificationAdded.next(this.createNotificationMessage(notification));
  }

  public updateNotification(notification: HbNotification) {
    this._notifications.getValue().splice(
      this._notifications.getValue().findIndex(x => x.id === notification.id),
      1,
      notification,
    );
    this._notifications.next(this._notifications.getValue());
    this._notificationAdded.next(this.createNotificationMessage(notification));
  }

  public removeNotification(notification: HbNotification) {
    this._notifications.getValue().splice(
      this._notifications.getValue().findIndex(x => x.id === notification.id),
      1,
    );
    this._notifications.next(this._notifications.getValue());
  }

  public clearNotifications(): void {
    this._notifications.next([]);
  }

  public hasNotifications(): boolean {
    return this._notifications.getValue().length > 0 ? true : false;
  }
}
